import React, { useEffect, useRef, useState } from 'react';
import { Pagination,Typography } from 'antd';
import {FileImageOutlined} from '@ant-design/icons';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '@mui/material';
import { ChevronLeft, ChevronRight } from '@mui/icons-material';

export type GalleryItem = {
  id: number;
  system_name?: string;
};

export interface CardSideBarItem extends GalleryItem {
  path: string;
  defects?: { x: number; y: number; w: number; h: number; confidence: number }[];
  timestamp: string;
  media_id: string;
  disposition: string;
}

export interface GalleryCard<T> {
  item: T;
  onNavigate: (id: number, system_name?: string) => void;
}

interface CardGalleryProps<T> {
  data: T[];
  totalPages: number;
  currentPage: number;
  onPageChange: (page: number, pageSize?: number) => void;
  cardClickPath: string | undefined;
  CardComponent: React.ComponentType<{
    item: T;
    onNavigate: (id: number, system_name?: string) => void;
  }>;
  itemsPerPage?: number;
  totalItems?: number;
  loading?: boolean;
  selectionMode?: boolean;
  selectedItems?:  Array<{id: string, system_name: string}>;
  onItemSelect?: (id: string, system_name: string) => void;
}

const CardGallery = <T extends GalleryItem>({
  data,
  totalPages,
  currentPage,
  onPageChange,
  cardClickPath,
  CardComponent,
  selectionMode = false,
  selectedItems = [],
  onItemSelect,
  itemsPerPage = 20,
  totalItems = totalPages * itemsPerPage,
  loading = false,
}: CardGalleryProps<T>) => {
  const navigate = useNavigate();
  const location = useLocation(); 
  const [searchParams, setSearchParams] = useSearchParams();
  const [pageSize, setPageSize] = useState(itemsPerPage);
  console.log("This is data", data)
  // Update URL query parameters when currentPage or pageSize changes
  useEffect(() => {
    const params = new URLSearchParams(searchParams);
    params.set('page', String(currentPage));
    params.set('limit', String(pageSize)); // Use 'limit' to match your API
    setSearchParams(params);
  }, [currentPage, pageSize, setSearchParams, searchParams]);

  // Handle both page change and page size change
  const handleChange = (page: number, size: number) => {
    setPageSize(size);
    onPageChange(page, size);
    console.log('Page change:', page, 'Page size:', size);
  };

  const handleItemClick = (item: T) => {
    if (selectionMode && onItemSelect) {
      // If in selection mode, perform selection
      onItemSelect(item.id.toString(), item.system_name || '');
    } else {
      // Otherwise, navigate
      const path = item.system_name
        ? `${cardClickPath}/${item.id}?system_name=${item.system_name}`
        : `${cardClickPath}/${item.id}`;
      navigate(path, {
        state: {
          items: data,
          currentPage,
          totalPages,
          system_name: item.system_name,
          from: location.pathname + location.search
        },
      });
    }
  };
  // Handle direct selection (when clicking on checkbox)
  const handleSelectionClick = (event: React.MouseEvent, id: string, system_name?: string) => {
    event.stopPropagation(); // Prevent the parent div's click handler
    if (onItemSelect) {
      onItemSelect(id, system_name || '');
    }
  };

  // Consistent way to check if an item is selected
   const isItemSelected = (item: T) => {
    return selectedItems.some(selected => {
      // Normalize system_name from BOTH sources to an empty string if it's missing.
      const selectedSystemName = selected.system_name || '';
      const itemSystemName = item.system_name || '';

      return selected.id === item.id.toString() && selectedSystemName === itemSystemName;
    });
  };

  return (
    <div className="p-5 bg-gray-500">
      <div className="grid grid-cols-[repeat(auto-fill,minmax(300px,1fr))] grid-rows-1 gap-5">
        {data.map((item) => (
          <div
            key={`${item.id}-${item.system_name}`}
            className={`relative ${selectionMode ? 'cursor-pointer' : ''} ${
              selectionMode && isItemSelected(item) ? 'ring-2 ring-blue-500 ring-offset-2' : ''
            }`}
            // Add the unified click handler here
            onClick={() => handleItemClick(item)}
          >
            {selectionMode && (
              <div
                className="absolute top-2 left-2 z-10 bg-white bg-opacity-80 rounded-md p-1"
                // This remains the same, it stops propagation
                onClick={(e) => handleSelectionClick(e, item.id.toString(), item.system_name || '')}
              >
                <input
                  type="checkbox"
                  checked={isItemSelected(item)}
                  className="h-5 w-5 text-blue-600 rounded"
                  readOnly
                />
              </div>
            )}
            <CardComponent
              item={item}
              // The onNavigate prop is now redundant
              onNavigate={() => {}}
            />
          </div>
        ))}
      </div>

      <div className="pagination fixed-pagination mt-4 flex justify-center">
        <Pagination
          total={totalItems}
          current={currentPage}
          onChange={handleChange}
          onShowSizeChange={handleChange}
          showSizeChanger={true}
          pageSizeOptions={['10', '20', '50', '100']}
          pageSize={pageSize}
          showTotal={(total, range) => `${range[0]}-${range[1]} of ${total} items`}
          disabled={loading}
        />
      </div>
    </div>
  );
};

export default CardGallery;


export interface CardSidebarProps<T extends CardSideBarItem> {
  data: T[];
  activeItem : T
  onNavigate: (id: number) => void;
}

export const CardSidebar = <T extends CardSideBarItem>({
  data,
  activeItem,
  onNavigate
}: CardSidebarProps<T>) => {
  // State for sidebar expanded/collapsed
  const [isExpanded, setIsExpanded] = useState(true);
  
  // Reference to the sidebar container
  const sidebarRef = useRef<HTMLDivElement>(null);
  
  // Toggle sidebar expansion
  const toggleSidebar = () => {
    setIsExpanded(!isExpanded);
    
    // Force recalculation of defect positions when sidebar is expanded
    if (!isExpanded) {
      // Small delay to let the sidebar expand first
      setTimeout(() => {
        // Trigger resize event to recalculate all image scaling
        window.dispatchEvent(new Event('resize'));
      }, 350); // Slightly longer than transition duration
    }
  };
  
  // Function to render a thumbnail with its defects and metadata
  const ThumbnailWithDefects = ({ item }: { item: T }) => {
    const imgRef = useRef<HTMLImageElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const [imgLoaded, setImgLoaded] = React.useState(false);
    const [defectPositions, setDefectPositions] = useState<Array<{x: number, y: number, w: number, h: number}>>([]);
    const [imageError, setImageError] = React.useState(false); // New state for image error
    
    // Calculate scale factor when the image is loaded or sidebar expands
    const updateDefectPositions = () => {
      if (imgRef.current && item.defects && item.defects.length > 0) {
        const img = imgRef.current;
        // Calculate scale factor based on the natural image size and displayed size
        const scaleX = img.clientWidth / img.naturalWidth;
        const scaleY = img.clientHeight / img.naturalHeight;
        
        // Update data attribute for debugging if needed
        containerRef.current?.setAttribute('data-scale', `${scaleX.toFixed(2)}x${scaleY.toFixed(2)}`);
        
        // Calculate all scaled positions
        const scaledPositions = item.defects.map(defect => ({
          x: defect.x * scaleX,
          y: defect.y * scaleY,
          w: defect.w * scaleX,
          h: defect.h * scaleY
        }));
        
        setDefectPositions(scaledPositions);
      }
    };
    
    // Recalculate when image loads
    useEffect(() => {
      if (imgLoaded) {
        updateDefectPositions();
      }
    }, [imgLoaded, item.defects]);
    
    // Listen for resize events (including our custom one)
    useEffect(() => {
      const handleResize = () => {
        if (imgLoaded && isExpanded) {
          updateDefectPositions();
        }
      };
      
      window.addEventListener('resize', handleResize);
      return () => window.removeEventListener('resize', handleResize);
    }, [imgLoaded, isExpanded]);
    
    // Also recalculate when the sidebar expands
    useEffect(() => {
      if (isExpanded && imgLoaded) {
        // Small delay to ensure DOM is updated
        setTimeout(updateDefectPositions, 50);
      }
    }, [isExpanded, imgLoaded]);
    
    // Handle image load
    const handleImageLoad = () => {
      setImgLoaded(true);
    };
    
    // Scroll active item into view
    useEffect(() => {
      if (item.id === activeItem?.id && containerRef.current) {
        containerRef.current.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest'
        });
      }
    }, [item.id, activeItem?.id]);
    
    // Format timestamp
    const formatTimestamp = (timestamp: string | undefined) => {
      if (!timestamp) return "N/A";
      try {
        const date = new Date(timestamp);
        return date.toLocaleString(undefined, {
          month: 'short',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit'
        });
      } catch (e) {
        return timestamp;
      }
    };
    
    return (
      <div
        ref={containerRef}
        className={`thumbnail-container w-full mb-4 border rounded overflow-hidden ${
          item.id === activeItem?.id ? 'border-blue-500 border-4' : 'border-gray-300'
        }`}
        onClick={() => onNavigate(item.id)}
      >
        {/* Image container */}
        <div className="image-wrapper relative overflow-hidden">
          {(!item.path || imageError) ? (
            <div className="flex flex-col items-center justify-center w-full h-full">
              <FileImageOutlined style={{ fontSize: "64px", color: "#d9d9d9" }} />
              <Typography.Text type="secondary" style={{ marginTop: "16px" }}>
                No Image Available
              </Typography.Text>
            </div>
          ) : (
            <>
              <img 
                ref={imgRef}
                src={item.path}
                alt={item.id.toString()}
                onLoad={handleImageLoad}
                onError={() => setImageError(true)}
                className="w-full h-auto object-contain"
              />
              
              {/* Render defect bounding boxes using pre-calculated positions */}
              {imgLoaded && defectPositions.map((position, index) => (
                <div
                  key={`defect-thumb-${item.id}-${index}`}
                  className="absolute border-red-500 border-2 pointer-events-none bg-red-200 bg-opacity-30"
                  style={{
                    left: `${position.x}px`,
                    top: `${position.y}px`,
                    width: `${position.w}px`,
                    height: `${position.h}px`,
                  }}
                />
              ))}
            </>
          )}
        </div>
        
        {/* Metadata section - tighter display */}
        <div className="metadata-container bg-gray-700 text-xs px-2 py-1 text-gray-100">
          <div className="grid grid-cols-5 gap-x-1">
            <span className="font-medium text-gray-300 col-span-1">ID:</span>
            <span className="truncate col-span-4" title={item.media_id}>{item.media_id}</span>
            
            <span className="font-medium text-gray-300 col-span-1">Time:</span>
            <span className="truncate col-span-4" title={formatTimestamp(item.timestamp)}>{item.timestamp}</span>
            
            <span className="font-medium text-gray-300 col-span-1">Status:</span>
            <span className={`truncate col-span-4 font-medium ${
              item.disposition === 'Scrap' ? 'text-red-400' : 
              item.disposition === 'Part Okay' ? 'text-green-400' :
              item.disposition === 'Rework' ? 'text-orange-400' :
              item.disposition === 'Skip' ? 'text-grey-400' : 'text-yellow-400'
            }`}>
              {item.disposition || 'pending'}
            </span>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="flex h-[90vh] relative">
      {/* Sidebar container with custom styling */}
      <div 
        ref={sidebarRef}
        className={`flex flex-col h-full bg-gray-800 shadow-lg transition-all duration-300 ease-in-out ${
          isExpanded ? 'w-[280px]' : 'w-0'
        }`}
        style={{
          overflowY: 'auto',
          overflowX: 'hidden',
          scrollbarWidth: 'thin',
          scrollbarColor: '#4B5563 #1F2937'
        }}
      >
        <div className="p-3 space-y-2">
          {/* Sidebar header */}
          <div className="flex justify-between items-center mb-4 text-gray-100 pb-2 border-b border-gray-700">
            <h3 className="font-medium">Image Gallery</h3>
            <span className="text-xs text-gray-400">{data.length} items</span>
          </div>
          
          {/* Thumbnail items - keep them rendered even when collapsed for better state persistence */}
          {data.map((item) => (
            <ThumbnailWithDefects key={item.id} item={item} />
          ))}
        </div>
      </div>
      
      {/* Toggle button */}
      <button
        onClick={toggleSidebar}
        className="absolute top-4 bg-blue-500 hover:bg-gray-600 text-white p-1 rounded-full shadow-md z-10 transition-all duration-300"
        style={{
          left: isExpanded ? '260px' : '5px',
        }}
      >
        {isExpanded ? <ChevronLeft /> : <ChevronRight/>}
      </button>
    </div>
  );
};
